import type { Observable } from 'rxjs'
import { of, forkJoin, from, catchError, map, tap, take } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import type { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router'
import { Router } from '@angular/router'
import { PermissionsService } from '@awork/features/workspace/services/permission-service/permissions.service'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { fetchTranslation } from '../_shared/functions/translation'
import type { HttpErrorResponse } from '@angular/common/http'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { PermissionsQuery } from '@awork/features/workspace/state/permissions.query'
import { AccountQuery } from '@awork/_shared/state/account.query'

@Injectable({ providedIn: 'root' })
export class FrameworkGuard {
  private router = inject(Router)
  private permissionsService = inject(PermissionsService)
  private permissionsQuery = inject(PermissionsQuery)
  private accountService = inject(AccountService)
  private accountQuery = inject(AccountQuery)
  private logService = inject(LogService)

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    let errorHandled = false

    const permissions = this.permissionsQuery.queryUserPermissions()
    const permissions$ = !this.permissionsService.permissionsFetched
      ? this.permissionsService.fetchPermissions()
      : of(permissions())

    const account = this.accountQuery.queryAccount()
    const account$ = this.accountService.accountFetched ? of(account()) : this.accountService.fetchAccountDetails()

    return forkJoin([
      permissions$.pipe(
        map(userPermissions => {
          // If the user is admin, allow everything
          if (userPermissions && userPermissions.isAdmin) {
            return true
          }

          const url = state.url // ex.: /users/overview

          // ex.: url -> users-overview
          const permission = url.substr(1).replace(/\//g, '-')

          // check if this route is NOT allowed
          if (userPermissions && !userPermissions.isAdmin && !userPermissions.isAllowedToSeeMenuItem(permission)) {
            this.router.navigate(['/not-allowed'])

            return false
          }

          return true
        }),
        take(1),
        catchError(error => {
          if (!errorHandled) {
            errorHandled = true

            return this.errorHandler(error)
          } else {
            return this.logError()
          }
        })
      ),
      account$.pipe(
        tap(() => {
          const translationPromise = fetchTranslation(false)
          return from(translationPromise())
        }),
        map(() => true),
        take(1),
        catchError(error => {
          if (!errorHandled) {
            errorHandled = true

            return this.errorHandler(error)
          } else {
            return this.logError()
          }
        })
      )
    ]).pipe(
      map(responses => {
        return !responses.some(response => !response)
      }),
      catchError(error => {
        if (!errorHandled) {
          errorHandled = true

          return this.errorHandler(error)
        } else {
          return this.logError()
        }
      })
    )
  }

  /**
   * Handles API call errors. Logout or refreshes the session
   * @param {HttpErrorResponse} error
   * @returns {Observable<boolean>}
   */
  private errorHandler(error: HttpErrorResponse): Observable<boolean> {
    if (error.status !== 401) {
      this.accountService.logout(true, null, null, {
        message: `Framework Guard failed with error ${error.status}`,
        error
      })
    } else {
      this.accountService.refreshSession().subscribe()
    }

    return of(false)
  }

  /**
   * Logs 'error handled' to LogDNA
   * @returns {Observable<boolean>}
   */
  private logError(): Observable<boolean> {
    this.logService.logMessage(Level.warn, 'Framework guard: Error already handled')

    return of(false)
  }
}
